import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Activity, X } from "lucide-react";
import { fmtRelative } from "@/lib/format";
import { Button } from "@/components/ui/button";

/** Iter 172 · Quick-view Brand activity log (popup from list view).
 *  Uses the unified activity endpoint /activity/brand/{bid}. */

const ACTION_TONE = {
  created: "bg-emerald-50 text-emerald-700 border-emerald-300",
  updated: "bg-sky-50 text-sky-700 border-sky-300",
  bulk_updated: "bg-indigo-50 text-indigo-700 border-indigo-300",
  ai_brief_applied: "bg-violet-50 text-violet-700 border-violet-300",
  deleted: "bg-rose-50 text-rose-700 border-rose-300",
};

export default function BrandActivityDialog({ brand, onClose }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!brand?.id) return;
    api.get(`/activity/brand/${brand.id}`, { params: { limit: 100 } })
       .then((r) => setRows(r.data || []))
       .catch((e) => { toast.error(apiErrorMessage(e, "Failed to load activity")); setRows([]); });
  }, [brand]);

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0"
                       data-testid="brand-activity-dialog">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-border">
          <div className="flex items-center gap-2">
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Activity — {brand?.name}
            </DialogTitle>
            <Button variant="ghost" className="ml-auto h-8 w-8 p-0 rounded-none" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {rows === null ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No activity yet for this brand.
            </div>
          ) : (
            <div className="divide-y divide-border" data-testid="brand-activity-list">
              {rows.map((a) => (
                <div key={a.id || a._id} className="py-2.5 flex items-start gap-3">
                  <div className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 shrink-0 ${ACTION_TONE[a.action] || "border-border text-muted-foreground"}`}>
                    {(a.action || "").replace("_", " ")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs">
                      <span className="font-medium">{a.actor_name || "System"}</span>
                      <span className="text-muted-foreground"> · {a.action} </span>
                      {a.target_label && (
                        <span className="text-muted-foreground">&ldquo;{a.target_label}&rdquo;</span>
                      )}
                    </div>
                    {a.metadata && Object.keys(a.metadata).length > 0 && (
                      <div className="text-[10px] text-muted-foreground mt-0.5 font-mono truncate">
                        {Object.entries(a.metadata).map(([k, v]) =>
                          `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`).join(" · ")}
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap"
                          title={a.created_at}>
                    {fmtRelative(a.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
